import { OptionType } from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { RaptorJobFormValues } from '@/raptorjob/presentation/RaptorJobValidationSchema';
import { useListCustomersAutocomplete } from '@/jobs/application/adapters/listCustomersAutocomplete';
import { useState, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { ALLOWED_CALLER_ID_TYPES_SET, ContactInfoType } from '@/customers/application/types/CustomerTypes';
import { ContactOption, useContactOptions } from '@/common/presentation/hooks/useContactOptions';
import { useGetCustomerCallerId } from '@/customers/application/adapters/getCustomerCallerId';
import type { CallerDto } from '@/libs/api-rest/generated/model';
import { findOptionByValue, hasOptionWithValue } from '@/common/presentation/utils/autocompleteUtils';

type CustomerOption = { id?: number | null; companyName?: string | null };

export const useERPCustomerInfoSection = (rawCaller?: string | null, callerInfo?: CallerDto | null) => {
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');

  const {
    options: customerOptions,
    loading: customerLoading,
    filterOptions: customerFilterOptions,
    rawCustomerData
  } = useListCustomersAutocomplete({ searchTerm: customerSearchTerm });

  const { values } = useFormikContext<RaptorJobFormValues>();

  const getSelectedCustomerData = (): CustomerOption | undefined => {
    if (!values.customerId || !rawCustomerData?.length) return undefined;
    return rawCustomerData.find((customer: CustomerOption) => customer.id === values.customerId);
  };

  const {
    loading: customerCallerLoading,
    contacts: customerContacts,
    customerDetails
  } = useGetCustomerCallerId(values.customerId ?? undefined, getSelectedCustomerData());

  const { options: contactOptions, initialCallerId } = useContactOptions({
    contacts: customerContacts,
    rawCaller: rawCaller ?? undefined,
    allowedTypes: ALLOWED_CALLER_ID_TYPES_SET
  });

  // Enhanced matching: Try to find contact ID using callerInfo (name + phone) for more reliable matching
  const matchedCallerId = useMemo(() => {
    // If we already have a valid initialCallerId (not 0), use it
    if (initialCallerId != null && initialCallerId !== 0 && initialCallerId !== '0') {
      return initialCallerId;
    }

    // If callerInfo is available, try to match by name and phone
    if (callerInfo?.name && callerInfo?.primaryPhone && customerContacts && customerContacts.length > 0) {
      const normalizePhone = (phone: string) => phone.replace(/\D/g, ''); // Remove non-digits
      const normalizeName = (name: string) => name.trim().toLowerCase();

      const callerNameNormalized = normalizeName(callerInfo.name);
      const callerPhoneNormalized = normalizePhone(callerInfo.primaryPhone);

      // Search through contacts to find a match
      for (const contact of customerContacts) {
        const contactInfos = contact.contactInformation ?? [];
        for (const info of contactInfos) {
          // Check if this contact info matches the callerInfo
          if (info?.type && ALLOWED_CALLER_ID_TYPES_SET.has(info.type as ContactInfoType)) {
            const contactName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
            const contactNameNormalized = normalizeName(contactName);
            const contactPhoneNormalized = info.contact ? normalizePhone(info.contact) : '';

            // Match by name and phone
            if (
              contactNameNormalized === callerNameNormalized &&
              contactPhoneNormalized &&
              contactPhoneNormalized === callerPhoneNormalized &&
              info.id != null
            ) {
              return info.id;
            }
          }
        }
      }
    }

    return initialCallerId;
  }, [initialCallerId, callerInfo, customerContacts]);

  const customerInitialOption = useMemo((): OptionType | undefined => {
    if (!values.customerId) return undefined;

    const selectedCustomerInOptions = findOptionByValue(customerOptions, values.customerId);

    if (selectedCustomerInOptions) return selectedCustomerInOptions as OptionType;

    const selectedCustomerData =
      values.customerId && rawCustomerData?.length
        ? rawCustomerData.find((customer: CustomerOption) => customer.id === values.customerId)
        : undefined;

    const customerName = values.customerName ?? selectedCustomerData?.companyName ?? customerDetails?.companyName ?? 'Unknown Customer';

    return {
      value: values.customerId,
      label: customerName
    };
  }, [customerOptions, values.customerId, values.customerName, customerDetails?.companyName, rawCustomerData]);

  const customerOptionsWithInitial = useMemo(() => {
    if (!customerInitialOption) return customerOptions;

    if (!hasOptionWithValue(customerOptions, customerInitialOption.value)) {
      return [...customerOptions, customerInitialOption];
    }

    return customerOptions;
  }, [customerOptions, customerInitialOption]);

  const getContactAutocompleteOptions = (): OptionType[] => {
    return (
      contactOptions.reduce((acc: ContactOption[], opt: ContactOption) => {
        if (!acc.some((o) => o.name === opt.name)) {
          acc.push(opt);
        }
        return acc;
      }, []) ?? []
    ).map((opt) => ({ value: opt.id, label: opt.name }));
  };

  const getContactInitialOption = (): OptionType | undefined => {
    const callerIdToUse = matchedCallerId ?? initialCallerId;
    if (callerIdToUse == null) return undefined;
    const found = getContactAutocompleteOptions().find((o) => String(o.value) === String(callerIdToUse));
    return found;
  };

  return {
    setCustomerSearchTerm,
    customerOptions: customerOptionsWithInitial,
    customerLoading,
    customerFilterOptions,
    customerCallerLoading,
    customerContacts,
    getContactAutocompleteOptions,
    getContactInitialOption,
    customerInitialOption,
    values,
    initialCallerId: matchedCallerId ?? initialCallerId
  };
};
